import React, { useState, useCallback, useRef, useEffect } from 'react';
import { DeepChat } from 'deep-chat-react';
import styled from 'styled-components';
import { IconButton } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { useChat } from '../../hooks/useChat';
import { useAuthContext } from '../../routes/login/AuthContext';




const BubbleContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
`;

const ChatContainer = styled.div<{ isOpen: boolean; isFullscreen: boolean }>`
  position: ${props => props.isFullscreen ? 'fixed' : 'absolute'};
  bottom: ${props => props.isFullscreen ? '0' : '70px'};
  right: ${props => props.isFullscreen ? '0' : '0'};
  top: ${props => props.isFullscreen ? '0' : 'auto'};
  left: ${props => props.isFullscreen ? '0' : 'auto'};
  width: ${props => props.isFullscreen ? '100vw' : '450px'};
  height: ${props => props.isFullscreen ? '100vh' : '650px'};
  background: white;
  border-radius: ${props => props.isFullscreen ? '0' : '12px'};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: ${props => props.isOpen ? 'block' : 'none'};
  overflow: hidden;
  z-index: ${props => props.isFullscreen ? '10000' : 'auto'};

  /* Mobile breakpoint - below 900px (md breakpoint) */
  @media (max-width: 899px) {
    position: fixed;
    bottom: 0;
    right: 0;
    top: 0;
    left: 0;
    width: 100vw;
    /* Fallback for browsers that don't support dvh */
    height: 100vh;
    /* Use dvh (dynamic viewport height) for iOS Safari compatibility */
    height: 100dvh;
    border-radius: 0;
    z-index: 10000;
  }

  /* iOS Safari specific fixes */
  @supports (-webkit-touch-callout: none) {
    @media (max-width: 899px) {
      /* Keep full viewport height; child handles keyboard inset to avoid double subtraction */
      height: 100dvh;
      min-height: -webkit-fill-available;
    }
  }
`;

const BubbleButton = styled(IconButton)`
  && {
    width: 60px;
    height: 60px;
    background-color: #0052CC;
    color: white;
    box-shadow: 0 4px 16px rgba(33, 103, 226, 0.3);
    
    &:hover {
      background-color: #1a56c7;
      transform: scale(1.05);
    }
    
    transition: all 0.2s ease-in-out;
  }
`;

const ChatHeader = styled.div`
  background-color: #0052CC;
  color: white;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 18px;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const FullscreenButton = styled(IconButton)`
  /* Hide fullscreen button on mobile devices */
  @media (max-width: 899px) {
    display: none !important;
  }
`;

const ChatContent = styled.div<{ isFullscreen: boolean }>`
  height: calc(100% - 48px);

  deep-chat {
    border: none;
    width: ${props => props.isFullscreen ? '100vw !important' : '450px'};
    height: ${props => props.isFullscreen ? 'calc(100vh - 70px) !important' : '595px !important'};
  }

  /* Mobile breakpoint - below 900px (md breakpoint) */
  @media (max-width: 899px) {
    deep-chat {
      /* Fallback */
      height: calc(100vh - 70px) !important;
      /* Prefer dvh when available */
      height: calc(100dvh - 70px) !important;
      width: 100vw !important;
    }
  }

  /* iOS Safari specific fixes */
  @supports (-webkit-touch-callout: none) {
    @media (max-width: 899px) {
      deep-chat {
        /* Use keyboard inset to keep input visible above keyboard */
        height: calc(100dvh - 70px - env(keyboard-inset-height, 0px)) !important;
        min-height: calc(-webkit-fill-available - 70px) !important;
      }
    }
  }
`;

export const ChatBubble: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { sendMessage, isAuthenticated, resetConversation } = useChat();
  const { session } = useAuthContext();
  const chatRef = useRef<any>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);


  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setIsFullscreen(false);
  }, []);

  const handleFullscreenToggle = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Effect to handle session changes and clear storage on page reload
  useEffect(() => {
    if (session) {
      try {
        // Type assertion to access the session methods
        const userId = (session as any).getIdToken().payload.sub;

        // Only clear storage on first initialization (page reload) or user change
        if (!hasInitialized || (currentUserId && currentUserId !== userId)) {
          // Clear any existing chat storage to ensure fresh start
          localStorage.removeItem(`roomsync-chat-session-${userId}`);
          localStorage.removeItem('deep-chat-storage');
          localStorage.removeItem('roomsync-chat-storage');

          // If this is a different user, clear the old storage
          if (currentUserId && currentUserId !== userId) {
            localStorage.removeItem(`roomsync-chat-session-${currentUserId}`);
          }

          // Reset conversation context
          resetConversation();

          setHasInitialized(true);
        }

        setCurrentUserId(userId);
      } catch (error) {
        // If we can't get user ID, just continue without clearing
        // console.warn('Could not extract user ID from session:', error);
      }
    } else if (currentUserId) {
      // User logged out, clear the current user ID
      setCurrentUserId(null);
    }
  }, [session, currentUserId, hasInitialized, resetConversation]);

  // Effect to handle layout adjustments when fullscreen mode changes
  useEffect(() => {
    if (chatRef.current && isOpen) {
      // Use setTimeout to ensure the DOM has updated with new dimensions
      const timeoutId = setTimeout(() => {
        // Trigger a scroll to bottom to ensure proper positioning after layout change
        if (chatRef.current?.scrollToBottom) {
          chatRef.current.scrollToBottom();
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isFullscreen, isOpen]);

  const chatHandler = useCallback(async (body: any, signals: any) => {
    try {
      // Extract the user's message from the body
      const userMessage = body.messages?.[body.messages.length - 1]?.text || '';

      const result = await sendMessage(userMessage);

      if (result.error) {
        signals.onResponse({ error: result.error });
      } else if (result.text) {
        signals.onResponse({ text: result.text });
      } else {
        signals.onResponse({
          error: 'Unexpected response format. Please try again.'
        });
      }

    } catch (error) {
      // console.error('Chat handler error:', error);
      signals.onResponse({
        error: 'Sorry, I encountered an unexpected error. Please try again.'
      });
    }
  }, [sendMessage]);

  // Don't render the chat bubble if user is not logged in
  if (!isAuthenticated) {
    return null;
  }

  return (
    <BubbleContainer>
      <ChatContainer isOpen={isOpen} isFullscreen={isFullscreen}>
        <ChatHeader>
          <span>RoomSync Assistant</span>
          <HeaderButtons>
            <FullscreenButton
              size="small"
              onClick={handleFullscreenToggle}
              sx={{ color: 'white' }}
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </FullscreenButton>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: 'white' }}
              title="Close chat"
            >
              <CloseIcon />
            </IconButton>
          </HeaderButtons>
        </ChatHeader>
        <ChatContent isFullscreen={isFullscreen}>
          <DeepChat
            ref={chatRef}
            connect={{
              handler: chatHandler,
            }}
            browserStorage={{
              key: `roomsync-chat-session-${currentUserId || 'anonymous'}`,
              maxMessages: 1000
            }}
            introMessage={{
              text: "Hi! I'm your RoomSync Assistant. How can I help you today?"
            }}
            textInput={{
              placeholder: {
                text: "Type your message here..."
              }
            }}
            chatStyle={{
              borderRadius: isFullscreen ? '0' : '0 0 12px 12px',
              fontFamily: 'inherit',
              width: '100%',
              fontSize: '16px',
            }}
          />
        </ChatContent>
      </ChatContainer>
      
      <BubbleButton onClick={handleToggle}>
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </BubbleButton>
    </BubbleContainer>
  );
};
